import { Divider, Stack, Typography } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC } from 'react';
import CreateForm from '../../../../../../../components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from '../../../../../../../context/ApplicationContext';
import { FORM_FIELDS_TYPES } from '../../../../../../../lib/constants/constant';
import { FormStrucItem } from '../../../../../../../lib/types/types';
import { NotFutureDateValidation, RadioButtonValidation, Required, ValidDate } from '../../../../../../../lib/validation/ValidationTypes';
import { mapCustomFormStrucIndex } from '../../../../../../../utils/formStruc';
import { checkReadOnlyInUeberarbeitung, isTrue } from '../../../../../../../utils/helper';

// import schemas
import Schema from '../../../../../../../lib/paths/antrag_FULL_Paths.json';
const P = Schema.attributes;

const BereitsBeantragteBeihilfenGlobal: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);
	return (
		<>
			<Typography variant='h3'>
				Angaben zu <u>bereits beantragten</u> weiteren De-minimis-Beihilfen
			</Typography>

			<Stack>
				<Typography>Im Folgenden geht es um De-minimis-Beihilfen im Sinne folgender Verordnungen:</Typography>
				<ul>
					<li>Allgemeine-De-minimis-Beihilfen</li>
					<li>Agrar-De-minimis-Beihilfen</li>
					<li>Fisch-De-minimis-Beihilfen</li>
				</ul>
				<Typography>Weitere Informationen und Details zu den Verordnungen finden Sie im Bereich "II. Definitionen und Erläuterungen".</Typography>
				<Divider sx={{ mt: '1rem' }} />
			</Stack>

			<CreateForm
				formStruc={formStrucMinimisBereitsBeantragtCheckbox()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>

			<CreateForm
				formStruc={formStrucBeantragteBeihilfenTabelle(application)}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
		</>
	);
};

export default BereitsBeantragteBeihilfenGlobal;

// Hiermit bestätige(n) ich/wir, dass das von mir/uns vertretene Unternehmen sowie im Sinne der Verordnung (EU) 2023/2831 mit ihm verbundenen Unternehmen in einem Zeitraum von drei Jahren über die beantragte De-minimis-Beihilfe hinaus
const formStrucMinimisBereitsBeantragtCheckbox = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Deminimis_Deminimis_Beihilfe_Bereits_Beantragt,
					type: FORM_FIELDS_TYPES.radio,
					validation: [{ validateFunction: RadioButtonValidation }],
					label: 'Hiermit bestätige ich/wir, dass das von mir/uns vertretene Unternehmen sowie im Sinne der Verordnung (EU) 2023/2831 mit ihm verbundenen Unternehmen in einem Zeitraum von drei Jahren über die beantragte De-minimis-Beihilfe hinaus',
					options: [
						{
							value: false,
							label: 'zum jetzigen Zeitpunkt <u>keine</u> weiteren De-minimis-Beihilfen im Sinne der o.g. Verordnungen hinaus beantragt haben.',
						},
						{
							value: true,
							label: 'zum jetzigen Zeitpunkt folgende De-minimis-Beihilfen bereits beantragt haben, die aber noch nicht bewilligt sind.',
						},
					],
					direction: 'column',
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};

// Tabelle
// Zum jetzigen Zeitpunkt wurden folgende De-minimis-Beihilfen bereits beantragt, die aber noch nicht bewilligt wurden.
const formStrucBeantragteBeihilfenTabelle = (application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Deminimis_Bereits_Beantragte_Beihilfen,
					type: 'componentTable',
					// tableLabel: 'Zum jetzigen Zeitpunkt wurden folgende De-minimis-Beihilfen bereits beantragt, die aber noch nicht bewilligt wurden.',
					fieldProps: {
						Unternehmen: { tooltip: 'Unternehmen oder Unternehmensverbund gemäß Art. 2 Abs. 2 VO 2023/2831' },
						Datum: { label: 'Datum des Antrags', width: '11rem' },
						Beihilfegeber: { width: '12rem' },
						Kennzeichen: { width: '9rem' },
						Form_Der_Beihilfe: { tooltip: '(z. B. Zuschuss, Darlehen, Bürgschaft…)' },
						vorausstl_Foerdersumme: { format: 'currency', maxWidth: '7rem' },
						vorausstl_Subventionswert: {
							format: 'currency',
							maxWidth: '7rem',
							tooltip: 'Höhe der öffentlichen Zuwendung; bei Zuschüssen entspricht der gesamte Zuschussbetrag dem Subventionswert.',
						},
					},
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	if (isTrue(get(application, P.Deminimis_Deminimis_Beihilfe_Bereits_Beantragt.path))) {
		return struc;
	} else {
		return [];
	}
};

// customFormStruc for specific validation in componentTable
const customFormStrucBeantragteBeihilfenTabelle = (application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				P.Deminimis_Bereits_Beantragte_Beihilfen.schema.attributes.Beihilfegeber,
				P.Deminimis_Bereits_Beantragte_Beihilfen.schema.attributes.Form_Der_Beihilfe,
				P.Deminimis_Bereits_Beantragte_Beihilfen.schema.attributes.Kennzeichen,
				P.Deminimis_Bereits_Beantragte_Beihilfen.schema.attributes.Unternehmen,
				{
					...P.Deminimis_Bereits_Beantragte_Beihilfen.schema.attributes.Datum,
					validation: [{ validateFunction: Required }, { validateFunction: ValidDate }, { validateFunction: NotFutureDateValidation }],
				},
				P.Deminimis_Bereits_Beantragte_Beihilfen.schema.attributes.vorausstl_Foerdersumme,
				P.Deminimis_Bereits_Beantragte_Beihilfen.schema.attributes.vorausstl_Subventionswert,
			],
		},
	];

	if (isTrue(get(application, P.Deminimis_Deminimis_Beihilfe_Bereits_Beantragt.path))) {
		return struc;
	} else {
		return [];
	}
};
export const getFormStrucDeMinimisBeantragteBeihilfen = (application: any) => {
	// const tabelleStruc = getFormStrucComponentTable(application, formStrucBeantragteBeihilfenTabelle());
	let tabelleStruc: FormStrucItem[] = mapCustomFormStrucIndex(
		application,
		customFormStrucBeantragteBeihilfenTabelle(application),
		P.Deminimis_Bereits_Beantragte_Beihilfen,
	);
	return concat(formStrucMinimisBereitsBeantragtCheckbox(), tabelleStruc);
};
